import { Vector2 } from "../../../common/vector2.js";
import { CatmulromPrecomputed } from "../../utils/catmulrom_precomputed.js";
import { RATE, FIREWORKS_COLORS } from "./fireworks_constants.js";
import { PayloadEmitter } from "../../core/emitters/payload_emitter.js";
import { FireworksColor } from "./fireworks_types.js";
import { VfxFireworksBatch } from "./vfx_fireworks_batch.js";
import { ImgID } from "../../../image_loader/img_id.js";

export class VfxFireworks_Explosion
{
  private static readonly LIFE_SPAN          : number = 500;
  private static readonly PARTICLE_LIFE_SPAN : number = 500;
  private static readonly RATE               : number = RATE;
  private static readonly COLOR_BREAKPOINT   : number = 0.2;
  private static readonly CIRCLE_COUNT       : number = 6;
  private static readonly LINE_COUNT         : number = 12;
  private static readonly EXPLOSION_RADIUS   : number = 280;
  private static readonly MIN_SIZE           : number = 20;
  private static readonly MAX_SIZE           : number = 35;
  private static readonly BASE_AMPLITUDE     : number = 30;

  public static spawn( batch: VfxFireworksBatch, center: Vector2, color_type: FireworksColor, now: number ): void
  {
    const color = FIREWORKS_COLORS.get( color_type );
    if ( !color ) throw new Error( `No value found for key: ${color_type}` );

    const scale = LayoutScaler.getScale();
    const explosion_radius = VfxFireworks_Explosion.EXPLOSION_RADIUS * scale;
    const circle_margin = explosion_radius / VfxFireworks_Explosion.CIRCLE_COUNT;
    const line_angle = 2 * Math.PI / VfxFireworks_Explosion.LINE_COUNT;

    // Precompute the ray directions (normals) for the 12 lines to save trig operations
    const ray_dirs: Vector2[] = [];
    for ( let i = 0; i < VfxFireworks_Explosion.LINE_COUNT; i++ )
    {
      const angle = i * line_angle;
      ray_dirs.push( new Vector2( Math.cos( angle ), Math.sin( angle ) ) );
    }

    const scratch_to = new Vector2( 0, 0 );

    for ( let margin = circle_margin, phase_idx = 0;
      margin <= explosion_radius;
      margin += circle_margin, phase_idx++ )
    {
      // phase alternates between 0 and line_angle/2
      const phase = (phase_idx % 2 === 0) ? 0 : line_angle / 2;
      const cos_phase = Math.cos( phase );
      const sin_phase = Math.sin( phase );

      for ( let i = 0; i < VfxFireworks_Explosion.LINE_COUNT; i++ )
      {
        const base_dir = ray_dirs[i];
        
        // Rotate base_dir by phase
        const dir_x = base_dir.x * cos_phase - base_dir.y * sin_phase;
        const dir_y = base_dir.x * sin_phase + base_dir.y * cos_phase;
        
        scratch_to.set( center.x + dir_x * margin, center.y + dir_y * margin );

        const precomputed = CatmulromPrecomputed.calcPrecomputed(
          center,
          scratch_to,
          VfxFireworks_Explosion.BASE_AMPLITUDE,
          { ax_mult: 0, ay_mult: -25 },
        );
        
        // Calculate approximate spline length
        const dx = scratch_to.x - center.x;
        const dy = scratch_to.y - center.y;
        const dist = Math.hypot( dx, dy );
        const particle_count = Math.max( 1, Math.floor( dist * ( VfxFireworks_Explosion.RATE / scale ) ) );
        
        // The original staggering for outward burst effect
        const line_start_time = now + margin * 0.2;
        
        for ( let p = 0; p < particle_count; p++ )
        {
          const curve_t = particle_count > 1 ? p / (particle_count - 1) : 0.5;
          const start_time = line_start_time + curve_t * VfxFireworks_Explosion.LIFE_SPAN;
          
          batch.spawnParticle(
            ImgID.CircleGlow1,
            precomputed.p1, precomputed.bp1, precomputed.bp2, precomputed.p2,
            new Vector2( 0, 0 ),
            curve_t,
            color,
            VfxFireworks_Explosion.MAX_SIZE * scale,
            VfxFireworks_Explosion.MIN_SIZE * scale,
            1.0,
            0.0,
            VfxFireworks_Explosion.COLOR_BREAKPOINT,
            0.0, // additive=0, batch handles it
            start_time,
            VfxFireworks_Explosion.PARTICLE_LIFE_SPAN
          );
        }
      }
    }
  }
}
